import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVideoSource } from '@/core/components/MediaRenderer/helper';
import { BdsIcon } from '@/core/constants/webComponents';

type PlaybackPhase = 'ready' | 'feedback' | 'hidden' | 'paused';

const FEEDBACK_FADE_DELAY_MS = 600;
const FEEDBACK_HIDE_DELAY_MS = 900;
const LAZY_LOAD_MARGIN = '200px';

type FullscreenCapableElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
type FullscreenCapableVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };
type FullscreenCapableDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
};

function enterFullscreen(container: HTMLElement, video: HTMLVideoElement | null): void {
  const containerAny = container as FullscreenCapableElement;
  const request = containerAny.requestFullscreen?.bind(container) ?? containerAny.webkitRequestFullscreen?.bind(container);
  if (request) {
    Promise.resolve(request()).catch((error) => console.error('Unable to enter fullscreen', error));
    return;
  }

  const videoAny = video as FullscreenCapableVideo | null;
  if (videoAny?.webkitEnterFullscreen) {
    videoAny.webkitEnterFullscreen();
    return;
  }

  console.error('Fullscreen is not supported');
}

function exitFullscreen(container: HTMLElement): void {
  if (document.fullscreenElement !== container) return;
  const docAny = document as FullscreenCapableDocument;
  const exit = docAny.exitFullscreen?.bind(document) ?? docAny.webkitExitFullscreen?.bind(document);
  Promise.resolve(exit?.()).catch((error) => console.error('Unable to exit fullscreen', error));
}

interface HlsVideoPreviewProps {
  src: string;
  mimeType?: string;
  alt: string;
  onFailure: () => void;
}

export const HlsVideoPreview: React.FC<HlsVideoPreviewProps> = ({ src, mimeType, alt, onFailure }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(() => typeof IntersectionObserver === 'undefined');
  const [phase, setPhase] = useState<PlaybackPhase>('ready');
  const [isFading, setIsFading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const feedbackTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const isPendingPlayRef = useRef(false);
  const failedRef = useRef(false);

  const handleFailure = (error?: unknown): void => {
    if (failedRef.current) return;
    failedRef.current = true;
    if (error !== undefined) console.error('HLS video playback failed', error);
    feedbackTimeoutsRef.current.forEach(clearTimeout);
    const container = containerRef.current;
    if (container) exitFullscreen(container);
    onFailure();
  };

  const videoRef = useVideoSource(isInView ? src : '', mimeType, () => handleFailure());

  useEffect(() => {
    if (isInView) return undefined;
    const node = containerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setIsInView(true);
      },
      { rootMargin: LAZY_LOAD_MARGIN },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isInView]);

  useEffect(() => () => {
    feedbackTimeoutsRef.current.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleFullscreenChange = () => {
      const fullscreenDocument = document as FullscreenCapableDocument;
      const fullscreenElement = document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement;
      setIsFullscreen(fullscreenElement === container);
    };

    container.addEventListener('fullscreenchange', handleFullscreenChange);
    container.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      container.removeEventListener('fullscreenchange', handleFullscreenChange);
      container.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const scheduleFeedbackHide = (): void => {
    feedbackTimeoutsRef.current.forEach(clearTimeout);
    const fadeTimeout = setTimeout(() => setIsFading(true), FEEDBACK_FADE_DELAY_MS);
    const hideTimeout = setTimeout(() => {
      setIsFading(false);
      setPhase('hidden');
    }, FEEDBACK_HIDE_DELAY_MS);
    feedbackTimeoutsRef.current = [fadeTimeout, hideTimeout];
  };

  const handleTogglePlay = (): void => {
    const video = videoRef.current;
    if (!video?.getAttribute('src') || isPendingPlayRef.current) return;

    if (!video.paused) {
      video.pause();
      return;
    }

    isPendingPlayRef.current = true;
    video
      .play()
      .catch((error) => handleFailure(error))
      .finally(() => {
        isPendingPlayRef.current = false;
      });
  };

  const handlePlay = (): void => {
    feedbackTimeoutsRef.current.forEach(clearTimeout);
    setIsFading(false);
    setPhase('feedback');
    scheduleFeedbackHide();
  };

  const handlePause = (): void => {
    feedbackTimeoutsRef.current.forEach(clearTimeout);
    setIsFading(false);
    setPhase('paused');
  };

  const handleEnded = (): void => {
    feedbackTimeoutsRef.current.forEach(clearTimeout);
    setIsFading(false);
    setPhase('ready');
  };

  const handleFullscreenClick = (): void => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement === container) {
      exitFullscreen(container);
      return;
    }
    const video = videoRef.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
    enterFullscreen(container, video);
  };

  const controlsVisible = phase !== 'hidden';
  const isPlayIcon = phase !== 'paused';

  return (
    <div ref={containerRef} className="classification-card__hls-video-wrapper">
      {isInView && (
        <video
          ref={videoRef}
          className="classification-card__hls-video"
          aria-label={alt}
          playsInline
          preload="none"
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onError={() => handleFailure(new Error('Unable to decode HLS video'))}
        >
          <track kind="captions" />
        </video>
      )}

      <button
        type="button"
        className="classification-card__hls-toggle"
        onClick={handleTogglePlay}
        aria-label={t('classificationCard.toggleVideoPlayback')}
      >
        <span
          className={`classification-card__hls-icon-circle${controlsVisible ? ' classification-card__hls-icon-circle--visible' : ''}${isFading ? ' classification-card__hls-icon-circle--fading' : ''}`}
        >
          <BdsIcon name={isPlayIcon ? 'play' : 'pause'} theme="outline" size="medium" color="surface-0" aria-hidden="true" />
        </span>
      </button>

      {controlsVisible && (
        <button
          type="button"
          className={`classification-card__hls-fullscreen${isFullscreen ? ' classification-card__hls-fullscreen--expanded' : ''}${isFading ? ' classification-card__hls-fullscreen--fading' : ''}`}
          onClick={handleFullscreenClick}
          aria-label={t(isFullscreen ? 'classificationCard.exitFullscreen' : 'classificationCard.enterFullscreen')}
        >
          <BdsIcon name={isFullscreen ? 'screen-fill' : 'screen-full'} theme="outline" size={isFullscreen ? 'large' : 'small'} color="surface-0" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
